import { memo, useMemo } from "react";
import { BarStatisticDiagram } from "ui/molecules/StatisticDiagram";
import {
    esTransactionsByCompanyPerPSPQueryToken,
    useStatisticDashboardContext
} from "ui/organizms/StatisticDashboard/StatisticDashboardProvider";
import type { BarDatum } from "@nivo/bar";
import type { AxisTickProps, BarTooltipProps } from "../shared/types";
import { randomColor } from "util/support";
import { CurrencySymbol } from "consts/general";
import BarTooltip from "../shared/BarTooltip";
import AxisBottomTick from "../shared/AxisBottomTick";
import ChartLegend, { type ChartLegendItem } from "../shared/ChartLegend";
import { fNumber } from "util/formaters";
import { getCategoricalColor } from "../../support/helpers";

const isMorField = ([key, value]: [string, string | number]) => (
    !key.endsWith('Color') &&
    !Number.isNaN(Number(value))
);

const getAmountFromDatum = (barDatum: BarDatum): number =>
    Object.entries(barDatum).reduce((acc, [key, value]) => {
        if (isMorField([key, value])) {
            return Number(acc) + Number(value);
        }

        return acc;
    }, 0);

const getMorNamesFromDatum = (barDatum: BarDatum): string =>
    Object.entries(barDatum).reduce((acc, [key, value]) => {
        if (isMorField([key, value])) {
            return [acc, key]
                .filter(Boolean)
                .join();
        }

        return acc;
    }, '');

const ProcessedAmountsByCompanyPerPSP = () => {
    const statisticDashboard = useStatisticDashboardContext();
    const { payload, isLoading } = statisticDashboard[esTransactionsByCompanyPerPSPQueryToken];

    const { data, keys, pspSize, legendItems } = useMemo(() => {
        const keysSet = new Set<string>();
        const pspSet = new Set<string>();

        if (!payload) {
            return {
                data: [] as BarDatum[],
                keys: [] as string[],
                pspSize: 0,
                legendItems: [] as ChartLegendItem[]
            };
        }

        const processedAmountByPSPMap = payload.rows.reduce((pspMap, row) => {
            const amount = Number(row.at(0));
            const psp = String(row.at(-1));
            const mor = String(row.at(-2));

            pspSet.add(psp);
            keysSet.add(mor);

            return pspMap.set(psp, {
                ...pspMap.get(psp),
                PSP: psp,
                [mor]: amount,
                [`${mor}Color`]: randomColor()
            });
        }, new Map<string, BarDatum>());

        const data = Array.from(processedAmountByPSPMap.values());
        const keys = Array.from(keysSet);

        const legendItems: ChartLegendItem[] = keys.map((key, index) => ({
            id: key,
            color: getCategoricalColor(index),
            total: data.reduce<number>((sum, datum) => {
                const value = datum[key];
                return sum + (typeof value === 'number' ? value : 0);
            }, 0)
        }));

        return { data, keys, pspSize: pspSet.size, legendItems };
    }, [payload]);

    const calculateAxisBottomTickRotation = () => pspSize > 4 ? 30 : 0;

    const getBottomTooltipProps = ({ value }: AxisTickProps): BarTooltipProps => {
        const {
            companies,
            totalAmount
        } = data
            .reduce((acc, barDatum) => {
                if (Object.is(barDatum.PSP, value)) {
                    return {
                        totalAmount: Number(acc.totalAmount) + getAmountFromDatum(barDatum),
                        companies: [acc.companies, getMorNamesFromDatum(barDatum)]
                            .filter(Boolean)
                            .join()
                    };
                }

                return acc;
            }, {
                companies: '',
                totalAmount: 0
            });

        return {
            label: `${companies} - ${value}`,
            formattedValue: fNumber(totalAmount, ',.2f'),
            postfix: CurrencySymbol.EUR
        } as BarTooltipProps;
    };

    return (
        <BarStatisticDiagram
            sx={{
                position: 'relative',
                overflow: 'visible'
            }}
            isLoading={isLoading}
            isDataAvailable={Boolean(data.length)}
            title='Processed Amounts (Company)'
            subheader='Per PSP'
            data-test-id="dashboard.ProcessedAmountsByCompanyPerPSP"
            legendSlot={!isLoading && (
                <ChartLegend
                    items={legendItems}
                    postfix={CurrencySymbol.EUR}
                    width={130}
                />
            )}
            chartProps={{
                style: {
                    maxHeight: 600,//478,
                    height: '100%'
                },
                keys,
                indexBy: 'PSP',
                margin: { top: 50, right: 150, bottom: 50, left: 60 },
                // padding: 0.3,
                valueScale: { type: 'linear' },
                indexScale: {
                    type: 'band',
                    round: true
                },
                colors: { scheme: 'category10' },
                valueFormat: " >-,.2f",
                /*defs: [
                    {
                        id: 'dots',
                        type: 'patternDots',
                        background: 'inherit',
                        color: '#ea9b57',
                        size: 4,
                        padding: 1,
                        stagger: true
                    },
                    {
                        id: 'lines',
                        type: 'patternLines',
                        background: 'inherit',
                        color: '#eed312',
                        rotation: -45,
                        lineWidth: 6,
                        spacing: 10
                    }
                ],
                fill: [
                    {
                        match: {
                            id: MOR.Default
                        },
                        id: 'dots'
                    },
                    // {
                    //     match: {
                    //         id: 'sandwich'
                    //     },
                    //     id: 'lines'
                    // }
                ],*/
                enableGridX: true,
                enableLabel: false,
                borderColor: {
                    from: 'color',
                    modifiers: [
                        [
                            'darker',
                            1.6
                        ]
                    ]
                },
                tooltip: props => (
                    <BarTooltip
                        {...props}
                        postfix={CurrencySymbol.EUR}
                    />
                ),
                axisTop: null,
                axisRight: null,
                axisBottom: {
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: calculateAxisBottomTickRotation(),
                    // legend: 'PSP',
                    legendPosition: 'middle',
                    legendOffset: 32,
                    renderTick: props => (
                        <AxisBottomTick
                            {...props}
                            isLoading
                            renderTitle={props => {
                                const {
                                    label,
                                    formattedValue,
                                    postfix
                                } = getBottomTooltipProps(props);

                                return `${label}: ${formattedValue} ${postfix}`;
                            }}
                        />
                    )
                },
                axisLeft: {
                    format: '.2s',
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: 0,
                    legend: `Amounts (${CurrencySymbol.EUR})`,
                    legendPosition: 'middle',
                    legendOffset: -45
                },
                labelSkipWidth: 12,
                labelSkipHeight: 12,
                labelTextColor: {
                    from: 'color',
                    modifiers: [
                        [
                            'darker',
                            1.6
                        ]
                    ]
                },
                role: 'application',
                ariaLabel: 'Processed Amounts (Company) Per PSP',
                barAriaLabel: e => `${e.id}: ${e.formattedValue} per PSP ${e.indexValue}`,
                data
            }}
        />
    );
};

export default memo(ProcessedAmountsByCompanyPerPSP);
